import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './PatientTab.css';

/**
 * @typedef {Object} PatientInfo
 * @property {string} id - 환자 ID
 * @property {string} name - 환자 이름
 * @property {Object} data - 환자 상세 정보
 */

/**
 * @typedef {Object} CacheData
 * @property {PatientInfo} data - 환자 정보
 * @property {number} lastUpdated - 마지막 업데이트 시간
 */

const PatientTab = ({ 
  patientId, 
  patientInfo, 
  onTabChange,
  setSelectedPatientInfo,
  onTabClose,
  cachedData, 
  setCachedData
}) => {
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // 캐시 데이터 유효성 검사
  const isValidCache = (cache) => {
    return cache && 
           cache.data && 
           cache.lastUpdated && 
           (Date.now() - cache.lastUpdated < 30 * 60 * 1000); // 30분 유효기간
  };

  // 캐시 데이터 업데이트
  const updateCache = (id, data) => {
    if (!id || !data) return;
    
    setCachedData(prev => ({
      ...prev,
      [id]: {
        data,
        lastUpdated: Date.now()
      }
    }));
  };

  // 캐시 데이터 제거
  const removeCache = (id) => {
    if (!id) return;
    
    setCachedData(prev => {
      const newCache = { ...prev };
      delete newCache[id];
      return newCache;
    });
  };

  // 캐시 클린업 함수
  const cleanupCache = useCallback(() => {
    try {
      console.log('🧹 Starting cache cleanup');
      const now = Date.now();
      setCachedData(prev => {
        const newCache = { ...prev };
        let cleanedCount = 0;
        
        Object.keys(newCache).forEach(id => {
          if (now - newCache[id].lastUpdated >= 30 * 60 * 1000) {
            delete newCache[id];
            cleanedCount++;
          }
        });
        
        if (cleanedCount > 0) {
          console.log(`🗑️ Cleaned up ${cleanedCount} expired cache entries`);
        }
        return newCache;
      });
    } catch (error) {
      console.error('Error in cache cleanup:', error);
    }
  }, [setCachedData]);

  // 주기적 캐시 클린업 설정
  useEffect(() => {
    const cleanup = setInterval(cleanupCache, 5 * 60 * 1000); // 5분마다 정리
    console.log('⏰ Set up periodic cache cleanup');
    
    return () => {
      clearInterval(cleanup);
      console.log('🛑 Cleared cache cleanup interval');
    };
  }, [cleanupCache]);

  useEffect(() => {
    if (!patientId || !patientInfo) return;

    try {
      const existingTabIndex = tabs.findIndex(tab => tab.patientId === patientId);
      
      if (existingTabIndex === -1) {
        // 새로운 탭 추가
        const newTab = {
          patientId,
          name: patientInfo.name,
          data: patientInfo
        };
        
        setTabs(prevTabs => [...prevTabs, newTab]);
        setActiveTab(tabs.length);
        updateCache(patientId, patientInfo);
        
        onTabChange(patientId, patientInfo);
        setSelectedPatientInfo(patientInfo);
      } else {
        // 기존 탭 선택
        const existingTab = tabs[existingTabIndex];
        const cachedPatientData = cachedData?.[patientId];
        
        // 중복 데이터 처리 방지
        if (existingTab && 
            existingTab.patientId === patientId && 
            JSON.stringify(existingTab.data) === JSON.stringify(patientInfo)) {
          setActiveTab(existingTabIndex);
          return;
        }

        setActiveTab(existingTabIndex);
        
        // 유효한 캐시가 있는 경우 사용
        if (isValidCache(cachedPatientData)) {
          onTabChange(patientId, cachedPatientData.data);
          setSelectedPatientInfo(cachedPatientData.data);
        } else {
          // 캐시가 없거나 만료된 경우 새로운 데이터로 업데이트
          updateCache(patientId, patientInfo);
          onTabChange(patientId, patientInfo);
          setSelectedPatientInfo(patientInfo);
        }
      }
    } catch (error) {
      console.error('Error in PatientTab effect:', error);
    }
  }, [patientId, patientInfo]);

  const handleTabChange = (event, newValue) => {
    try {
      if (newValue < 0 || newValue >= tabs.length) {
        console.warn('Invalid tab index:', newValue);
        return;
      }

      setActiveTab(newValue);
      const selectedTab = tabs[newValue];
      
      if (!selectedTab) {
        console.warn('Selected tab not found:', newValue);
        return;
      }

      onTabChange(selectedTab.patientId, selectedTab.data);
      setSelectedPatientInfo(selectedTab.data);
    } catch (error) {
      console.error('Error in handleTabChange:', error);
    }
  };

  const handleTabClose = (event, tabToClose) => {
    try {
      event.stopPropagation();

      const tabToRemove = tabs[tabToClose];
      if (tabToRemove) {
        removeCache(tabToRemove.patientId);
      }

      const updatedTabs = tabs.filter((_, index) => index !== tabToClose);
      setTabs(updatedTabs);

      const newActiveTab = activeTab >= tabToClose 
        ? (activeTab > 0 ? activeTab - 1 : 0) 
        : activeTab;
      
      setActiveTab(newActiveTab);

      if (updatedTabs.length > 0) {
        const lastTab = updatedTabs[newActiveTab];
        onTabChange(lastTab.patientId, lastTab.data);
        setSelectedPatientInfo(lastTab.data);
      } else {
        onTabChange(null, null);
        setSelectedPatientInfo(null);
      }
    } catch (error) {
      console.error('Error in handleTabClose:', error);
    }
  };

  // 모든 탭을 제거하는 함수
  const removeAllTabs = () => {
    try {
      // 모든 탭의 캐시 제거
      tabs.forEach(tab => {
        removeCache(tab.patientId);
      });

      // 탭 목록 초기화
      setTabs([]);
      setActiveTab(0);
      onTabChange(null, null);
      setSelectedPatientInfo(null);
    } catch (error) {
      console.error('Error in removeAllTabs:', error);
    }
  };

  return (
    <div className="patient-tab-container">
      <Box className="patient-tab-header">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            flex: 1,
            minHeight: '40px',
            '& .MuiTabs-scroller': {
              width: '100%',
              overflow: 'hidden'
            },
            '& .MuiTabs-flexContainer': {
              width: '100%',
              gap: '2px'
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.patientId}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography className="patient-tab-name">
                    {tab.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleTabClose(e, index)}
                    className="patient-tab-close-button"
                  >
                    <CloseIcon sx={{ fontSize: 'calc(16px * var(--scale-factor))' }} />
                  </IconButton>
                </Box>
              }
              sx={{
                minHeight: '40px',
                padding: 'calc(8px * var(--scale-factor))',
                textTransform: 'none',
                backgroundColor: activeTab === index ? '#ffffff' : '#f5f5f5',
                borderRadius: 'calc(4px * var(--scale-factor)) calc(4px * var(--scale-factor)) 0 0',
                marginRight: '2px',
                '&:hover': {
                  backgroundColor: activeTab === index ? '#ffffff' : '#e5e7eb',
                },
                '&.Mui-selected': {
                  color: '#2563eb',
                }
              }}
            />
          ))}
        </Tabs>
        {tabs.length > 0 && (
          <IconButton
            size="small"
            onClick={removeAllTabs}
            className="patient-tab-remove-all-button"
          >
            <CloseIcon sx={{ fontSize: 'calc(16px * var(--scale-factor))' }} />
          </IconButton>
        )}
      </Box>
      <div className="patient-tab-content">
        {tabs.map((tab, index) => (
          <div
            key={tab.patientId}
            className="patient-tab-panel"
            role="tabpanel"
            hidden={activeTab !== index}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientTab;
